import { Check, Plus, Download, Trash2, AlertCircle } from 'lucide-react';

/**
 * Buttons Section - Button variant comparison
 *
 * Tests:
 * - Primary, secondary, danger buttons
 * - Icon buttons
 * - Loading states
 * - Disabled states
 * - Size variations
 */
export default function ButtonsSection({ theme }) {
  const isDark = theme === 'dark';

  const variants = [
    { label: 'Primary', color: 'blue', icon: Check },
    { label: 'Success', color: 'green', icon: Plus },
    { label: 'Warning', color: 'amber', icon: AlertCircle },
    { label: 'Danger', color: 'red', icon: Trash2 },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Button Comparison
        </h2>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Current vs iOS-style buttons with enhanced shadows and bolder text
        </p>
      </div>

      {/* Size Comparison */}
      <div className="space-y-6">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Button Sizes
        </h3>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Current */}
          <div>
            <div className={`text-xs font-semibold uppercase tracking-wider mb-4 ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Current Design
            </div>
            <div className="space-y-3">
              <button className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">
                Large Button
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">
                Medium Button
              </button>
              <button className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 transition-colors">
                Small Button
              </button>
            </div>
          </div>

          {/* iOS-Style */}
          <div>
            <div className={`text-xs font-bold uppercase tracking-wider mb-4 ${
              isDark ? 'text-blue-400' : 'text-blue-600'
            }`}>
              iOS-Style (Enhanced)
            </div>
            <div className="space-y-3">
              <button className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl">
                Large Button
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-all shadow-md hover:shadow-lg">
                Medium Button
              </button>
              <button className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm font-bold hover:bg-blue-700 transition-all shadow-sm hover:shadow-md">
                Small Button
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Variant Comparison */}
      <div className="space-y-6">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Button Variants
        </h3>

        {variants.map(variant => (
          <div key={variant.label} className="grid md:grid-cols-2 gap-8">
            {/* Current */}
            <div>
              {variant.label === 'Primary' && (
                <div className={`text-xs font-semibold uppercase tracking-wider mb-4 ${
                  isDark ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Current Design
                </div>
              )}
              <button className={`px-4 py-2 bg-${variant.color}-600 text-white rounded-lg font-medium hover:bg-${variant.color}-700 transition-colors flex items-center gap-2`}>
                <variant.icon size={18} />
                {variant.label} Action
              </button>
            </div>

            {/* iOS-Style */}
            <div>
              {variant.label === 'Primary' && (
                <div className={`text-xs font-bold uppercase tracking-wider mb-4 ${
                  isDark ? 'text-blue-400' : 'text-blue-600'
                }`}>
                  iOS-Style (Enhanced)
                </div>
              )}
              <button className={`px-4 py-2 bg-${variant.color}-600 text-white rounded-lg font-bold hover:bg-${variant.color}-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2`}>
                <variant.icon size={18} strokeWidth={2.5} />
                {variant.label} Action
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Secondary/Outline Buttons */}
      <div className="space-y-6">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Secondary Buttons
        </h3>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Current */}
          <div>
            <button className={`px-4 py-2 border-2 ${
              isDark
                ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-100'
            } rounded-lg font-medium transition-colors`}>
              Secondary Action
            </button>
          </div>

          {/* iOS-Style */}
          <div>
            <button className={`px-4 py-2 border-2 ${
              isDark
                ? 'border-blue-500 text-blue-400 hover:bg-blue-900/20'
                : 'border-blue-600 text-blue-600 hover:bg-blue-50'
            } rounded-lg font-bold transition-all shadow-sm hover:shadow-md`}>
              Secondary Action
            </button>
          </div>
        </div>
      </div>

      {/* States */}
      <div className="space-y-6">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Button States
        </h3>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Current */}
          <div className="space-y-3">
            <button disabled className="px-4 py-2 bg-gray-400 text-gray-200 rounded-lg font-medium cursor-not-allowed">
              Disabled State
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium opacity-50 cursor-wait flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Loading...
            </button>
          </div>

          {/* iOS-Style */}
          <div className="space-y-3">
            <button disabled className="px-4 py-2 bg-gray-400 text-gray-200 rounded-lg font-bold cursor-not-allowed shadow-sm">
              Disabled State
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg font-bold opacity-75 cursor-wait shadow-md flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Loading...
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
